// __EQUIP_COMPAT__ / __EQUIP_V2__ (2026-10-04) The equipment warning at every assign path.
//
//   const [gateEquipment, equipmentModal] = useEquipmentGate();
//   const eq = await gateEquipment({ driverId, loads | loadIds, stopIds, truckId, date, added, context });
//   if (!eq.proceed) return;                 // Cancel: nothing is assigned
//   ... write the assignment ...             // and render {equipmentModal}
//
// Henry: "When cargo is being assigned over the limits there should be a warning.
// Sometimes we can negotiate those." When the check finds anything above a hint the
// window lists every issue (equipment, temporary restrictions, half-load season, the
// driver's day / HOS):
//   warnings only -> "Assign anyway" is one tap; a reason is optional
//   a block issue -> "Assign anyway" needs a typed reason (Settings > Equipment rules)
//   a stop issue  -> cannot be assigned (the driver or his unit no longer exists)
// Assigning anyway is recorded BEFORE the assignment is written (billing_data
// .equipment_override with the accepted warnings, and the audit log); if that fails,
// nothing is assigned.
import React, { useCallback, useState } from 'react';
import { checkAssignment, recordOverride } from '../services/equipmentService';
import { buildOverride, OVERRIDE_MIN_REASON } from '../utils/equipmentCompat';

const SEV = {
  stop: { color: 'var(--color-danger)', label: 'Cannot assign: ' },
  block: { color: 'var(--color-danger)', label: 'Needs a reason: ' },
  warn: { color: 'var(--color-warning)', label: 'Warning: ' },
  info: { color: 'var(--text-muted)', label: 'Note: ' },
};

export function EquipmentIssueList({ result, compact, hideInfo }) {
  if (!result) return null;
  const list = [...(result.stops || []), ...(result.blocking || []), ...(result.warnings || []), ...(hideInfo ? [] : (result.infos || []))];
  return (
    <div style={{ fontSize: compact ? '0.78rem' : '0.86rem' }}>
      {result.unit && <div style={{ color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Unit: <strong>{result.unit.label}</strong> ({result.unit.typeLabel})</div>}
      {result.season && <div style={{ color: 'var(--color-warning)', marginBottom: '0.3rem' }}>Half-load season {result.season.start} to {result.season.end}{result.season.note ? ' - ' + result.season.note : ''}</div>}
      {result.unchecked && <div style={{ color: 'var(--color-warning)' }}>Equipment could not be read just now - not checked.</div>}
      <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
        {list.map((x, i) => (
          <li key={i} style={{ color: (SEV[x.severity] || SEV.warn).color, marginBottom: compact ? '0.15rem' : '0.35rem' }}>
            <strong>{(SEV[x.severity] || SEV.warn).label}</strong>{x.message}
          </li>
        ))}
      </ul>
      {result.timingNote && <div style={{ color: 'var(--text-secondary)', marginTop: '0.3rem' }}>Day: {result.timingNote}</div>}
      {result.peakNote && <div style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>{result.peakNote}</div>}
      {result.unit && result.unit.notes && result.unit.notes.length > 0 && (
        <div style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>In effect: {result.unit.notes.join('; ')}</div>
      )}
      {!list.length && !result.unchecked && <div style={{ color: 'var(--color-success)' }}>Equipment fits.</div>}
    </div>
  );
}

export function EquipmentWarningModal({ result, title, onCancel, onConfirm, choices, pickedTruckId, onPick }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const stop = !result.canAssign;
  const needs = !!result.needsReason;
  const fits = !!result.ok;   // __EQUIP_ASSIGN__ shown only to ask which shared unit
  const ok = !stop && !busy && (!needs || reason.trim().length >= OVERRIDE_MIN_REASON);
  const go = async () => {
    setBusy(true); setErr(null);
    try { await onConfirm(reason.trim()); } catch (e) { setErr(e.message || String(e)); setBusy(false); }
  };
  const border = stop || needs ? 'var(--color-danger)' : 'var(--color-warning)';
  return (
    <div data-equipment-warning style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '1rem' }}>
      <div role="alertdialog" aria-modal="true" style={{ backgroundColor: 'var(--surface-1)', borderRadius: 'var(--radius-lg, 10px)', padding: '1.25rem', width: '100%', maxWidth: '600px', maxHeight: '88vh', overflow: 'auto', border: '2px solid ' + border }}>
        <h3 style={{ marginTop: 0, color: fits ? 'var(--accent-blue)' : border }}>{stop ? 'Cannot assign' : fits ? 'Which unit?' : 'Equipment / timing warning'}{title ? ' - ' + title : ''}</h3>
        {choices && choices.length > 1 && (
          <div data-unit-picker style={{ marginBottom: '0.7rem', padding: '0.5rem 0.6rem', borderRadius: '6px', backgroundColor: 'var(--surface-2)' }}>
            <div style={{ fontSize: '0.84rem', marginBottom: '0.3rem' }}>{(result.driverName || 'This driver') + ' shares several units - which one takes this job?'}</div>
            {choices.map((c) => (
              <label key={c.truckId} style={{ display: 'block', fontSize: '0.84rem', cursor: 'pointer', marginBottom: '0.15rem' }}>
                <input type="radio" name="eq-unit" checked={pickedTruckId === c.truckId} disabled={busy} onChange={() => onPick && onPick(c.truckId)} style={{ marginRight: '0.4rem' }} />
                {c.label}{c.primary ? ' (primary)' : ''}
              </label>
            ))}
          </div>
        )}
        <p style={{ marginTop: 0, fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
          {stop ? 'This assignment is not possible:' : fits ? 'The equipment fits on that unit.' : 'Check these before assigning ' + (result.driverName || 'this driver') + ':'}
        </p>
        <EquipmentIssueList result={result} />
        {!stop && !fits && (
          <>
            <label style={{ display: 'block', marginTop: '0.9rem', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
              {needs ? 'A reason is needed to assign anyway (kept on the job and in the audit log):' : 'Reason (optional - kept on the job and in the audit log with what you accepted):'}
            </label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="e.g. overhang flagged; permit booked; customer's forklift unloads it"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: '0.3rem', padding: '0.5rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '5px' }} />
          </>
        )}
        {err && <div style={{ color: 'var(--color-danger)', fontSize: '0.82rem', marginTop: '0.4rem' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.9rem' }}>
          <button type="button" onClick={onCancel} disabled={busy} style={{ padding: '0.55rem 1.1rem', backgroundColor: 'var(--btn-secondary-bg, #7f8c8d)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>
            {stop ? 'Close' : 'Cancel - don\'t assign'}
          </button>
          {!stop && (
            <button type="button" onClick={go} disabled={!ok} title={ok ? '' : 'Type a reason first'} style={{ padding: '0.55rem 1.1rem', backgroundColor: ok ? border : 'var(--surface-3)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: ok ? 'pointer' : 'not-allowed', fontWeight: 'bold' }}>
              {busy ? 'Recording...' : fits ? 'Assign' : 'Assign anyway'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// gate({ driverId, loads | loadIds, stopIds, truckId, date, added, context, title, recordFor })
//   -> { proceed: false }                         cancelled (or cannot be assigned)
//   -> { proceed: true, override: null, result }  fits (hints only) or no driver
//   -> { proceed: true, override, result }        assigned anyway (override already recorded
//                                                 on recordFor || the job ids)
export function useEquipmentGate() {
  const [pending, setPending] = useState(null);
  const gate = useCallback(async (opts = {}) => {
    const { driverId, loads, loadIds, stopIds, truckId, date, added, context, title, recordFor } = opts;
    if (!driverId) return { proceed: true, override: null, result: null };
    let result;
    try {
      result = await checkAssignment({ driverId, loads, loadIds, stopIds, truckId, date, added });
    } catch (e) {
      console.warn('equipment check failed (assignment not blocked):', e);
      return { proceed: true, override: null, result: null, unchecked: true };
    }
    // __EQUIP_ASSIGN__ he shares several units and none is clearly his: ask which (unit picker)
    const choices = result.unitChoices || null;
    if (result.ok && !choices) return { proceed: true, override: null, result };
    const ids = recordFor || loadIds || (loads || []).map((l) => l.id).filter(Boolean);
    return new Promise((resolve) => setPending({ result, title, resolve, ids, context, driverId, choices, opts,
      pickedTruckId: result.pickedTruckId || null }));
  }, []);
  const pick = async (truckId) => {
    const p = pending;
    if (!p) return;
    try {
      const o = p.opts || {};
      const r = await checkAssignment({ driverId: o.driverId, loads: o.loads, loadIds: o.loadIds, stopIds: o.stopIds, truckId, date: o.date, added: o.added });
      setPending({ ...p, result: r, pickedTruckId: truckId });
    } catch (e) { console.warn('equipment re-check failed:', e); }
  };
  const close = () => setPending(null);
  const modal = pending ? (
    <EquipmentWarningModal
      key={pending.pickedTruckId || 'x'}
      result={pending.result}
      title={pending.title}
      choices={pending.choices}
      pickedTruckId={pending.pickedTruckId}
      onPick={pick}
      onCancel={() => { const p = pending; close(); p.resolve({ proceed: false, result: p.result }); }}
      onConfirm={async (reason) => {
        const p = pending;
        if (p.result.ok) { close(); p.resolve({ proceed: true, override: null, result: p.result, truckId: p.pickedTruckId || null }); return; }
        const override = buildOverride({ result: p.result, reason, context: p.context, driverId: p.driverId });
        const rec = await recordOverride({ loadIds: p.ids, override });
        if (!rec.ok) throw new Error('Could not record the override, so nothing was assigned: ' + rec.error);
        close();
        p.resolve({ proceed: true, override, result: p.result, truckId: p.pickedTruckId || null });
      }}
    />
  ) : null;
  return [gate, modal];
}

export default useEquipmentGate;
